"use client";

import { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { useTranslation } from "@/lib/i18n";

/* Loading skeleton — uses translations, must be a proper component */
function MeshLoadingSkeleton() {
  const { t } = useTranslation();
  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="text-center mb-8">
          <span className="text-xs font-semibold uppercase tracking-wider text-eyebrow">
            {t('mesh_eyebrow')}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[var(--foreground)]">
            {t('mesh_heading')}
          </h2>
        </div>
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="w-full lg:w-3/5 aspect-[16/10] max-h-[600px] rounded-2xl border border-[var(--border)] bg-[var(--surface-dark)] flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <div className="relative w-14 h-14">
                <div className="absolute inset-0 rounded-full border-2 border-accent/20 animate-ping" />
                <div className="absolute inset-2 rounded-full border-2 border-t-accent border-accent/10 animate-spin" />
                <div className="absolute inset-4 rounded-full bg-accent/10 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-accent" />
                </div>
              </div>
              <span className="text-xs text-[var(--muted-foreground)] font-mono">
                Initializing Network Mesh...
              </span>
            </div>
          </div>
          <div className="w-full lg:w-2/5 min-h-[300px]" />
        </div>
      </div>
    </section>
  );
}

/* Lightweight static placeholder while the section is below the fold or before
   the user has scrolled near. Keeps the layout reserved without paying the cost
   of Leaflet/Three.js. */
function MeshReservedSlot() {
  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8" aria-hidden="true">
      <div className="mx-auto max-w-7xl">
        <div className="w-full aspect-[16/10] max-h-[600px] rounded-2xl border border-[var(--border)] bg-[var(--surface-dark)]" />
      </div>
    </section>
  );
}

/* Lazy-load the 3D GlobalLiveMesh — heavy Three.js / R3F bundle, SSR disabled.
   On <lg (mobile/tablet) we render the lightweight 2D MobileNetworkMesh instead,
   saving ~150KB gzipped of Three.js/R3F and avoiding WebGL thermal throttling. */
const GlobalLiveMesh = dynamic(
  () =>
    import("@/components/GlobalLiveMesh").then((m) => ({
      default: m.GlobalLiveMesh,
    })),
  {
    ssr: false,
    loading: () => <MeshLoadingSkeleton />,
  }
);

const MobileNetworkMesh = dynamic(
  () =>
    import("@/components/MobileNetworkMesh").then((m) => ({
      default: m.MobileNetworkMesh,
    })),
  {
    ssr: false,
    loading: () => <MeshLoadingSkeleton />,
  }
);

/* Detect WebGL support — heuristic only, runs once on mount.
   Returns true when a WebGL context can be created on a probe canvas. */
function detectWebGL(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl2") ||
      canvas.getContext("webgl") ||
      canvas.getContext("experimental-webgl");
    return !!gl;
  } catch {
    return false;
  }
}

export function GlobalLiveMeshWrapper() {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [shouldMount, setShouldMount] = useState(false);
  const [webglOk, setWebglOk] = useState<boolean | null>(null);
  // Track desktop breakpoint at the JS level so we never mount the heavy
  // three.js GlobalLiveMesh on mobile/tablet. Previously the desktop branch
  // was only CSS-hidden via `hidden lg:block`, which still pulled the bundle
  // on small screens. `null` = unknown (SSR/pre-effect), used to gate render.
  const [isDesktop, setIsDesktop] = useState<boolean | null>(null);

  /* IntersectionObserver lazy-mount: defer Leaflet + Three.js bundles until the
     user scrolls within 400px of the mesh section. Cuts mobile TBT/LCP from the
     home page when the user lands above the fold. State is set only from async
     callbacks (observer + microtask fallback) to satisfy react-hooks/set-state-in-effect. */
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      // Old browser — mount eagerly rather than never. Defer via microtask to
      // avoid setState during effect body (lint: set-state-in-effect).
      const id = window.setTimeout(() => setShouldMount(true), 0);
      return () => window.clearTimeout(id);
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShouldMount(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "400px 0px" }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  /* Observe the lg breakpoint (1024 px in Tailwind defaults) so mount decisions
     are based on real viewport width, not CSS visibility. State is set only
     from the listener callback (async) to comply with set-state-in-effect. */
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(mq.matches);
    // Defer initial setState one microtask to keep it out of the effect body.
    const id = window.setTimeout(update, 0);
    mq.addEventListener("change", update);
    return () => {
      window.clearTimeout(id);
      mq.removeEventListener("change", update);
    };
  }, []);

  /* Probe WebGL once we decide to mount, so the desktop branch can fall back
     to the 2D Leaflet mesh on devices where WebGL is blocked or unsupported.
     Detection runs inside requestAnimationFrame to keep setState out of the
     effect body (lint: set-state-in-effect). */
  useEffect(() => {
    if (!shouldMount) return;
    if (isDesktop !== true) return; // WebGL probe only matters for desktop branch
    let cancelled = false;
    const raf = window.requestAnimationFrame(() => {
      if (cancelled) return;
      const ok = detectWebGL();
      setWebglOk(ok);
      if (!ok) {
        // GA4 event so we can quantify WebGL fallback rate in production.
        const w = window as unknown as { gtag?: (...args: unknown[]) => void };
        if (typeof w.gtag === "function") {
          w.gtag("event", "webgl_unsupported", { event_category: "mesh" });
        }
      }
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
    };
  }, [shouldMount, isDesktop]);

  return (
    <>
      {/* Anchor rendered immediately (not behind dynamic import) so the
          "Explore Network" button works even before the heavy Three.js bundle
          has loaded on desktop. scroll-mt offsets the fixed header. */}
      <div id="network-mesh" className="scroll-mt-24" aria-hidden="true" />
      {/* Sentinel that triggers the IntersectionObserver mount. Placed ~400px
          before the section visually. */}
      <div ref={sentinelRef} aria-hidden="true" />
      {!shouldMount || isDesktop === null ? (
        <MeshReservedSlot />
      ) : isDesktop ? (
        /* Desktop: full 3D WebGL globe — falls back to 2D Leaflet mesh when
           WebGL is unavailable (older devices, blocked contexts, headless). */
        webglOk === false ? (
          <MobileNetworkMesh />
        ) : (
          <GlobalLiveMesh />
        )
      ) : (
        /* Mobile + tablet: lightweight 2D Leaflet mesh with accordion PoP lists.
           three.js bundle is never fetched on these viewports. */
        <MobileNetworkMesh />
      )}
    </>
  );
}
